import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LogError, LogStatus, UserInfo } from '@memberjunction/core';
import { CommitteesEntity, DuesRecordsMetricsEntity } from 'mj_generatedentities';
import { map, Subject, takeUntil } from 'rxjs';
import { DataFetchService } from 'src/app/services/data-fetch.service';
import { ChapterDetailsMetrics, DataGridColumn, ServerSideFilter, SummaryCard } from 'src/app/utils/interfaces';
import { Utils } from 'src/app/utils/utils';

import { AcsDataGridComponent } from '../shared/acs-data-grid/acs-data-grid';
import { DropdownOption } from '../shared/acs-button/acs-button';
import { ChapterOfficerGridColumns, DuesGridColumns, GridExportOptions, MemberRosterGridColumns } from 'src/app/utils/grid-constants';
import { EntityNames, QueryNames } from 'src/app/utils/constants';
// import { UserCommitteeAccessService } from 'src/app/services/user-committee-access.service';

@Component({
  selector: 'acs-chapter-detail',
  templateUrl: './chapter-details.html',
  styleUrls: ['../../../styles/_page-layout.scss', './chapter-details-specific.scss']
})
export class ChapterDetailsComponent implements OnInit, OnDestroy {

  private destroy$: Subject<void> = new Subject<void>();
  
  @ViewChild('dataGrid') dataGrid!: AcsDataGridComponent;

  isLoading = true;
  error: string | null = null;
  chapterId: string | null = null;

  chapterEntity!: CommitteesEntity;

  // Summary data
  summary!: ChapterDetailsMetrics;
  
  summaryCards: SummaryCard[] = [];
  currentUser!: UserInfo;
  
   // Export dropdown options
  exportOptions = GridExportOptions;

  constructor(
    private dataFetchService: DataFetchService,
    private utils: Utils,
    private route: ActivatedRoute,
    private router: Router
    // private userCommitteeAccessService: UserCommitteeAccessService
  ) {}

  /*
   * EXAMPLE USAGE OF COMMITTEE ACCESS SERVICE:
   *
   * To filter data based on user's committee access:
   *
   * async loadChapterData() {
   *   // Wait for committee access to load
   *   await this.userCommitteeAccessService.refreshCommitteeAccess();
   *
   *   // Get committee access filter
   *   const committeeFilter = this.userCommitteeAccessService.getCommitteeAccessFilter('CommitteeID');
   *
   *   // Apply filter to data queries
   *   const result = await this.dataFetchService.fetchEntityData(
   *     'MembershipRosters',
   *     committeeFilter, // This ensures user only sees data for committees they have access to
   *     'Name ASC'
   *   );
   * }
   *
   * // Check if user has access to specific committee
   * canViewCommittee(committeeId: string): boolean {
   *   return this.userCommitteeAccessService.hasCommitteeAccess(committeeId);
   * }
   */
  
  activeTab: 'members' | 'officers' | 'dues' = 'members';
  entityName!: string;
  gridColumns: DataGridColumn[] = [];
  gridTitle!: string;
  gridDescription!: string;
  serverSideFilters: ServerSideFilter [] = [];

  ngOnInit(): void {
    this.route.queryParamMap.pipe(
        map(params => ({ id: params.get('id') })),
        takeUntil(this.destroy$)
    ).subscribe(({ id }) => {
        this.chapterId = id;
        if(!this.chapterId) {
            this.isLoading = false;
            return;
        }
        this.isLoading = true;
        this.loadChapterDetails();      
    });
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadChapterDetails(): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      // Load chapter data
      await this.loadChapter();
      this.loadSummaryData();
      this.onTabChange("members");  

    } catch (error) {
      this.error = 'Failed to load chapter details. Please try again.';
      console.error('Chapter detail load error:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private async loadChapter(): Promise<void> {
    try {
      if(!this.chapterId) return;
      LogStatus(`Chapter Detail - Loading chapter ${this.chapterId}`);
      const entity = await this.dataFetchService.fetchRecordById<CommitteesEntity>(EntityNames.Committees, this.chapterId);
      if (!entity) {
        this.error = 'Chapter not found';
        return;
      }
      this.chapterEntity = entity;
    } catch(error) {
      LogError('Chapter Detail - Error loading chapter:', undefined, error);
    }
  }
  
  onTabChange(tab: 'members' | 'officers' | 'dues'): void {
    this.activeTab = tab;
    
    switch(tab) {
      case 'members':
        this.gridColumns = MemberRosterGridColumns;
        this.entityName = EntityNames.MembershipRosters;
        this.serverSideFilters = [{ sql: `CommitteeID = ${this.chapterEntity.CommitteeID}` }];
        this.gridTitle = 'Member Details';
        this.gridDescription = 'Complete member information with contact details, membership status, and dues information. Click on any member row to view detailed information.';
        break;
      case 'officers':
        this.gridColumns = ChapterOfficerGridColumns;
        this.entityName = EntityNames.ChapterOfficers;
        this.serverSideFilters = [{ sql: `CommitteeID = ${this.chapterEntity.CommitteeID}` }];
        this.gridTitle = 'Chapter Officers';
        this.gridDescription = 'Current leadership positions and contact information.';
        break;
      case 'dues':
        this.gridColumns = DuesGridColumns;
        this.entityName = EntityNames.ChapterDuesSummaries;
        this.serverSideFilters = [{ sql: `CommitteeID = ${this.chapterEntity.CommitteeID}` }];
        this.gridTitle = 'Dues Records';
        this.gridDescription = 'Member dues and payment status for this chapter.';
        break;
    }
  }

  goBack(): void {
    this.router.navigate(['/chapters']);
  }

  onServerDataError(error: any): void {
    LogError(`Error loading ${this.entityName} data:`, undefined, error);
    this.error = `Failed to load ${this.entityName} data. Please try again.`;
  }

  private async loadSummaryData(): Promise<void> {
    // Initialize summary data - will be updated when data loads
    const result = await this.dataFetchService.executeQuery<ChapterDetailsMetrics>({
      QueryName: QueryNames.ChapterWiseMetricsSummary,
      Parameters: {
        CommitteeID: this.chapterEntity.CommitteeID
      }
    });
    if(result.success && result.data.length > 0) {
      this.summary = result.data[0];
    }
    else {
      this.summary = {
        CommitteeID: this.chapterEntity.CommitteeID,
        CommitteeName: this.chapterEntity.Name || '',
        TotalActiveMembers: 0,
        TotalFellows: 0,
        TotalAnnualDuesInvoiced: 0,
        Collections: 0
      }
    }

    this.updateSummaryCards();
  }

  private updateSummaryCards(): void {
    this.summaryCards = [
      {
        id: 'total-members',
        value: `${this.summary.TotalActiveMembers}`,
        label: this.summary.TotalActiveMembers === 1 ? 'Member Assigned' : 'Members Assigned',
        sublabel: 'ACS Members assigned to this Chapter',
        icon: '🏥',
        variant: 'primary',
        format: 'number'
      },
      {
        id: 'fellows',
        value: `${this.summary.TotalFellows}`,
        label: 'Fellows Paid',
        sublabel: 'ACS Fellows who paid Chapter Dues',
        icon: '👥',
        variant: 'success',
        format: 'number'
      },
      {
        id: 'chapter-dues',
        value: `${this.summary.TotalAnnualDuesInvoiced}`,
        label: 'Chapter Dues',
        sublabel: 'YTD $ Invoiced',
        icon: '💲',
        variant: 'warning',
        format: 'currency'
      },
      {
        id: 'chapter-collections',
        value: `${this.summary.Collections}`,
        label: 'Chapter Collections',
        sublabel: 'YTD $',
        icon: '💰',
        variant: 'info',
        format: 'currency'
      }
    ];
  }
  
  get userRole(): string {
    const systemRoles = ['UI', 'Developer', 'Integration'];
    return this.currentUser.UserRoles.map(roleEntity => roleEntity.Role).find(roleName => !systemRoles.includes(roleName)) || 'Member';
  }

  // Convenience method for template usage
  formatCurrency(amount: number): string {
    return this.utils.formatCurrency(amount);
  }
}